"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { Control, useFormContext } from "react-hook-form";

import FormPhoneField from "@/components/common/forms/FormPhoneField";
import FormTextField from "@/components/common/forms/FormTextField";
import { useAuth } from "@/contexts/AuthContext";
import { readUTMCookies } from "@/lib/utm-tracker";
import { FirstStepSchemaType } from "@/schemas/validation/sellCarFormSchema";

interface PersonalInformationStepProps {
  control: Control<FirstStepSchemaType>;
}

const PersonalInformationStep = ({ control }: PersonalInformationStepProps) => {
  const t = useTranslations("SellCar.form");
  const { reset, watch, setValue } = useFormContext<FirstStepSchemaType>();

  const { isAuthLoading, isAuthenticated, user } = useAuth();

  const marketingData = readUTMCookies();
  const marketingDataWatch = watch("marketing");

  useEffect(() => {
    if (!!marketingData && !marketingDataWatch) {
      setValue("marketing", marketingData);
    }
  }, [marketingData, marketingDataWatch, setValue]);

  useEffect(() => {
    if (isAuthenticated && user) {
      reset({
        name: user.name || "",
        phone: user.phone || "",
      });
    }
  }, [isAuthenticated, user, reset]);

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mt-10">
        <h4 className="mb-4 font-inter text-base font-bold text-primary-foreground">{t("contactDetails")}</h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormTextField
            name="name"
            control={control}
            placeholder={isAuthLoading ? t("loadingUser") : t("fullName")}
            required
            disabled={isAuthLoading || isAuthenticated}
          />
          <FormPhoneField
            name="phone"
            control={control}
            placeholder={isAuthLoading ? t("loadingUser") : t("contactNumber")}
            required
            disabled={isAuthLoading || isAuthenticated}
            className="rtl:placeholder:text-right"
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalInformationStep;
